import type {
  Duration,
  FacilityHierarchyConfig,
  ISO8601DateOnly,
  ISO8601Timestamp,
  Role,
  Schedule,
  ScheduleNotificationConfig,
  StandardTimestamps,
  TrackedStatus,
} from '../types/compliance.ts';
import {
  dateLocal,
  dateStrDurationAdd,
  duration,
  durationIsEntirelyPositive,
  timestamp,
} from '../../cross-app-shared/services/dates-and-durations.ts';

export const notificationEscalationPath = {
  environmentalManagers: duration({ days: -3 }),
  generalManagers: duration({}),
  corporateEnvironmentalManagers: duration({ days: 1 }),
};

export function facilityEscalationHierarchyDefault(): FacilityHierarchyConfig {
  return {
    environmentalManagers: {
      users: [],
      escalates: notificationEscalationPath.environmentalManagers,
      source: 'builtin',
      name: 'Facility Environmental Manager',
    },
    generalManagers: {
      users: [],
      escalates: notificationEscalationPath.generalManagers,
      source: 'builtin',
      name: 'Facility General Manager',
    },
    corporateEnvironmentalManagers: {
      users: [],
      escalates: notificationEscalationPath.corporateEnvironmentalManagers,
      source: 'builtin',
      name: 'Corporate Environmental Manager',
    },
  };
}

export function facilityEscalationHierarchyCreate(roles: Role[]): {
  hierarchy: FacilityHierarchyConfig;
  lastUpdatedAt: ISO8601Timestamp;
} {
  const hierarchy = facilityEscalationHierarchyDefault();
  let lastUpdatedAt = timestamp(new Date(0));

  for (const role of roles) {
    if (role.type === 'corporateEnvironmentalManagers') {
      hierarchy.corporateEnvironmentalManagers.users.push(...role.involved);
      hierarchy.corporateEnvironmentalManagers.roleID = role.roleID;
    } else if (role.type === 'environmentalManagers') {
      hierarchy.environmentalManagers.users.push(...role.involved);
      hierarchy.environmentalManagers.roleID = role.roleID;
    } else if (role.type === 'generalManagers') {
      hierarchy.generalManagers.users.push(...role.involved);
      hierarchy.generalManagers.roleID = role.roleID;
    } else if (role.type === 'normal' && role.escalates) {
      hierarchy[role.roleID] = {
        users: role.involved,
        escalates: role.escalates,
        source: 'role',
        name: role.name,
        mustBeAssignedToEscalate: role.mustBeAssignedToEscalate,
      };
    }

    if (role.updatedAt > lastUpdatedAt) {
      lastUpdatedAt = role.updatedAt;
    }
  }

  return { hierarchy, lastUpdatedAt };
}

export type NotificationDateItem = {
  date: ISO8601DateOnly;
  scheduleNotificationID:
    | ScheduleNotificationConfig['scheduleNotificationID']
    | 'created'
    | 'due'
    | 'overdue'
    | 'escalation';
};

/**
 * Based on a Schedule, this function produces a list of notifications based on the provided due date. Continues
 * producing notifications until one year after the due date or after the given `now` date, whichever is farther in the
 * future.
 */
export function scheduleToNotificationDates(
  schedule: Omit<Schedule, 'dueAtFirst' | 'frequency' | 'limit' | keyof StandardTimestamps>,
  due: ISO8601DateOnly,
  tracked: TrackedStatus,
  escalationHierarchy: {
    [Key in keyof FacilityHierarchyConfig]: { escalates: Duration | null };
  }, // TaskEscalationHierarchy | FacilityHierarchyConfig
  now = dateLocal(),
  produceUntil = duration({ years: 1 }),
  createdAtConcrete: ISO8601DateOnly | null = null,
): NotificationDateItem[] {
  // Guard against nonsensical task due dates:
  if (due < '1970-01-01') {
    due = '1970-01-01' as ISO8601DateOnly;
  }

  const result: NotificationDateItem[] = [];
  const createdAtInTheory = dateStrDurationAdd(due, schedule.create);
  const createdAt = createdAtConcrete ?? createdAtInTheory;
  const notifyOnCreate = schedule.notifyOnCreate ?? false;
  const notifyOnDue = tracked ? true : (schedule.notifyOnDue ?? true);

  // We'll produce dates until a year after now or a year after the due date, whichever is longer:
  const yearAfterDue = dateStrDurationAdd(due, produceUntil);
  const yearAfterNow = dateStrDurationAdd(now, produceUntil);
  let end = yearAfterNow > yearAfterDue ? yearAfterNow : yearAfterDue;

  const createdNotification: NotificationDateItem = {
    date: createdAt,
    scheduleNotificationID: 'created',
  };
  if (notifyOnCreate) {
    result.push(createdNotification);
  }

  const dueItem: NotificationDateItem = {
    date: due,
    scheduleNotificationID: 'due',
  };
  let dueItemPushed = false;

  // Instantiate the configs with specific dates, and sort them in newest-to-oldest as this will be helpful for
  // subsequent querying:
  const notificationConfigs = schedule.notifications
    .map((n) => {
      return {
        ...n,
        activates:
          n.activates === null
            ? createdAt < createdAtInTheory
              ? createdAt
              : createdAtInTheory
            : dateStrDurationAdd(due, n.activates),
      };
    })
    .sort((a, b) => b.activates.localeCompare(a.activates));

  const oneDay = duration({ days: 1 });

  if (notificationConfigs.length > 0) {
    // If there are some very late changes to the notification schedule, "end" might not encompass them. If that's the
    // case, extend our end date:
    const potentialLatestNotificationDate = dateStrDurationAdd(
      notificationConfigs[0].activates === createdAt ? due : notificationConfigs[0].activates,
      notificationConfigs[0].frequency,
    );
    if (potentialLatestNotificationDate >= end) {
      end = potentialLatestNotificationDate;
    }

    for (let current = createdAt; current <= end; current = dateStrDurationAdd(current, oneDay)) {
      if (current === due && notifyOnDue) {
        result.push(dueItem);
        dueItemPushed = true;
        continue;
      }

      const config = notificationConfigs.find((n) => n.activates <= current);
      if (!config) {
        continue;
      }

      if (!durationIsEntirelyPositive(config.frequency)) {
        continue;
      }

      // The created notification might not be in the array if they are disabled, but it is still a useful anchor:
      const lastNotification = result[result.length - 1] ?? createdNotification;
      // If applying that newly activated schedule results in a date _before_ that schedule is supposed to activate,
      // send the next notification on the activation date:
      let next = dateStrDurationAdd(lastNotification.date, config.frequency);
      if (next < config.activates) {
        next = config.activates;
      }

      if (current !== next) {
        continue;
      }

      result.push({
        date: current,
        scheduleNotificationID: config.scheduleNotificationID,
      });
    }
  }

  if (!dueItemPushed && notifyOnDue) {
    result.push(dueItem);
  }

  const datesSent = new Set(result.map((n) => n.date));

  if (tracked) {
    // Ensures we schedule a notification on every escalation date:
    for (const [escalationID, { escalates }] of Object.entries(escalationHierarchy)) {
      if (!escalates) {
        continue;
      }

      if (tracked === 'facility' && escalationID === 'corporateEnvironmentalManagers') {
        continue;
      }

      const escalationDate = dateStrDurationAdd(due, escalates);
      if (datesSent.has(escalationDate)) {
        continue;
      }

      datesSent.add(escalationDate);
      result.push({
        date: escalationDate,
        scheduleNotificationID: 'escalation',
      });
    }

    // Ensures we keep sending corporate or GM escalation emails each day after we escalate to the top:
    const topEscalationKey: 'corporateEnvironmentalManagers' | 'generalManagers' =
      tracked === 'facility' ? 'generalManagers' : 'corporateEnvironmentalManagers';
    for (
      let topEscalationDate = dateStrDurationAdd(
        due,
        escalationHierarchy[topEscalationKey].escalates || notificationEscalationPath[topEscalationKey],
      );
      topEscalationDate <= end;
      topEscalationDate = dateStrDurationAdd(topEscalationDate, oneDay)
    ) {
      if (datesSent.has(topEscalationDate)) {
        continue;
      }

      result.push({
        date: topEscalationDate,
        scheduleNotificationID: 'escalation',
      });
    }
  } else if (schedule.notifyOnDue ?? true) {
    // Ensure we keep sending overdue reminders for untracked tasks:
    for (
      let afterDueAt = dateStrDurationAdd(due, duration({ days: 1 }));
      afterDueAt <= end;
      afterDueAt = dateStrDurationAdd(afterDueAt, oneDay)
    ) {
      if (datesSent.has(afterDueAt)) {
        continue;
      }

      result.push({
        date: afterDueAt,
        scheduleNotificationID: 'overdue',
      });
    }
  }

  // Sorting to make sure we have the due date notification in the correct position:
  result.sort((a, b) => {
    return a.date.localeCompare(b.date);
  });

  return result;
}
